<template>
  <div class="relative">
    <input
      ref="inputRef"
      v-model="query"
      type="text"
      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
      :placeholder="placeholder"
      @input="debouncedSearch"
      @focus="showDropdown = true"
      @blur="handleBlur"
    />
    <div
      v-if="showDropdown && suggestions.length > 0"
      class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg"
    >
      <ul class="max-h-60 overflow-y-auto">
        <li
          v-for="item in suggestions"
          :key="item.id"
          class="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white text-sm"
          @mousedown.prevent="selectAddress(item)"
        >
          {{ item.place_name }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Search for an address...'
  }
});

const emit = defineEmits(['update:modelValue']);

const query = ref(props.modelValue);
const suggestions = ref([]);
const showDropdown = ref(false);
let searchTimeout = null;

// Ensure your VUE_APP_MAPBOX_TOKEN is injected from .env
const MAPBOX_TOKEN = window.__RUNTIME_CONFIG__?.VUE_APP_MAPBOX_TOKEN || process.env.VUE_APP_MAPBOX_TOKEN;

watch(() => props.modelValue, (newVal) => {
  if (newVal !== query.value) {
    query.value = newVal;
  }
});

const debouncedSearch = () => {
  emit('update:modelValue', query.value); // keep parent updated with raw text
  if (searchTimeout) clearTimeout(searchTimeout);
  
  if (!query.value || query.value.length < 3) {
    suggestions.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    if (!MAPBOX_TOKEN) {
      console.warn('No Mapbox token configured.');
      return;
    }
    try {
      const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query.value)}.json?access_token=${MAPBOX_TOKEN}&types=address,poi`);
      const data = await response.json();
      if (data && data.features) {
        suggestions.value = data.features;
        showDropdown.value = true;
      }
    } catch (e) {
      console.error('Error fetching address suggestions', e);
    }
  }, 300);
};

const selectAddress = (item) => {
  query.value = item.place_name;
  emit('update:modelValue', query.value);
  suggestions.value = [];
  showDropdown.value = false;
};

const handleBlur = () => {
  // Use timeout to allow mousedown.prevent on suggestions to fire first if .prevent fails for some reason
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
};
</script>
